import { GAME_ASSET_SLUG } from "@/config/game";
import { useTokenCosts } from "@/design-system/Evolution/useTokenCosts";
import { CardEffects } from "@/design-system/PlayerCard/CardEffects";
import { FIRST_OWNER_GLYPH } from "@/design-system/PlayerCard/PlayerCardView";
import { star } from "@/figmaElements";
import { cn } from "@/lib/utils";
import {
  contentLabelsSelector,
  eventTokensSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import { costTokens, formatEvoCost } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { CardFont, Cosmetic, Cost, Foil, PriceInfo } from "@repo/shared/types";
import { FACE_STAT_KEYS } from "@repo/shared/utils";
import { useSelector } from "react-redux";

import { convertPlayerPrice } from "../../../../functions/convertPlayerPrice";
import { extractColors } from "../../../../functions/extractCardColors";
import { getPriceDisplay } from "../../../../functions/getPriceDisplay";
import "../../../../icons";
import {
  EvolutionsIcon,
  owned_icon_true as ownedIcon,
  untradeable_icon_true as untradeableIcon,
} from "../../../../icons";
import { getVersionAssetsSelector } from "../../../../redux/loadedContent/loadedContentSlice";
import { chemStylesToIcon } from "../../../../redux/squadBuilder/squadBuilderUtils";
import { PlayStylePlus } from "../PlayStylePlus/PlayStylePlus";

type Props = {
  rating: number;
  chemistry?: {
    currentChemistry: number;
    previousChemistry: number;
    isOutOfPosition: boolean;
  };
  cardName: string;
  cardFont?: CardFont;
  foil?: Foil;
  cosmetics?: Cosmetic[];
  preferredPosition: string;
  possiblePositions: string[];
  clubId: number;
  countryId: number;
  leagueId: number;
  versionId: number;
  attributes: number[];
  playerUrl: string;
  priceInfo?: PriceInfo;
  isSbcPlayer: boolean;
  isObjectivePlayer: boolean;
  owned: boolean;
  untradeable: boolean;
  /** EA's first-owner glyph under the position. */
  firstOwner?: boolean;
  metalId: number;
  isMobile: boolean;
  hasDynamicImage: boolean;
  playStylesPlus: number[];
  hidePrice: boolean;
  skillMoves?: number;
  weakFoot?: number;
  chemStyleId?: number;
  largeMobile?: boolean;
  isEvoPlayer?: boolean;
  cost?: Cost;
  onClick?: () => void;
  lazyLoad?: boolean;
  /** Plays the card's effects once on arrival (see CardEffects). */
  playOnShow?: boolean;
};

const FC24Player = ({
  onClick,
  rating,
  chemistry,
  cardName,
  cardFont,
  foil,
  cosmetics,
  clubId,
  countryId,
  leagueId,
  attributes,
  playerUrl,
  preferredPosition,
  chemStyleId,
  priceInfo,
  owned,
  untradeable,
  firstOwner,
  isMobile,
  versionId,
  metalId,
  hasDynamicImage,
  playStylesPlus,
  hidePrice,
  largeMobile,
  isEvoPlayer,
  cost,
  skillMoves,
  weakFoot,
  lazyLoad = false,
  playOnShow,
}: Props) => {
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
  const versionAssets = useSelector(getVersionAssetsSelector);
  const { positions, playerAttributes } = useSelector(contentLabelsSelector);
  const eventTokens = useSelector(eventTokensSelector);
  const tokenCosts = useTokenCosts(costTokens(cost));
  const { t } = useTranslation("common");
  const { topText, bottomText, cardUrl, isLight, strip } = extractColors({
    versionAssets,
    versionId,
    metalId,
  });

  const { iconUrl, label, showNumber, displayPrice } = getPriceDisplay(
    priceInfo,
    eventTokens,
  );

  const playerImage = (
    <img
      // eslint-disable-next-line i18next/no-literal-string -- asset id, not copy
      alt="player-url"
      className={cn(
        "absolute flex items-center",
        largeMobile ? "w-[72px] top-[17%]" : "w-3/5 top-[20%]",
        hasDynamicImage && versionId !== 72
          ? cn(
              "w-full -top-1 mobile:right-0 mobile:w-28 touch-device:w-36",
              largeMobile ? "w-28 -top-1" : "w-full -top-1",
            )
          : "mobile:w-[72px] touch-device:w-24 mobile:top-[17%] touch-device:top-[15%] right-[10px] touch-device:right-4",
      )}
      src={playerUrl + "?v5"}
      loading={lazyLoad ? "lazy" : "eager"}
    />
  );

  const topLeftArea = (
    <div
      className={`absolute left-[14%] top-[22%] touch-device:left-[14%] flex flex-col leading-none`}
      style={{
        color: topText,
      }}
    >
      <div
        className={cn(
          "text-sm touch-device:text-xl touch-device:-mt-0.5 leading-4 touch-device:leading-6",
          largeMobile ? "-ml-0.5" : "mobile:-ml-0.5",
        )}
        style={{
          fontFamily: "CruyffMedium",
        }}
      >
        {rating}
      </div>
      <div
        className="mx-auto text-xtiny touch-device:text-tiny touch-device:-mt-0.5"
        style={{
          fontFamily: "CruyffCondensedMedium",
        }}
      >
        {positions[preferredPosition]?.name ?? preferredPosition}
      </div>
      {!isMobile && chemStyleId != null && chemStyleId in chemStylesToIcon && (
        <div
          className="mx-auto text-tiny touch-device:text-xs"
          style={{ fontFamily: "UltimateTeamIcons" }}
        >
          {chemStylesToIcon[chemStyleId]}
        </div>
      )}
      {firstOwner && (
        <div
          className="mx-auto text-tiny touch-device:text-xs"
          style={{ fontFamily: "UltimateTeamIcons" }}
        >
          {FIRST_OWNER_GLYPH}
        </div>
      )}
    </div>
  );

  const priceSection = (
    <div className="w-full flex absolute mobile:top-1 top-[-6px] touch-device:top-0">
      <div
        className={cn(
          "flex flex-row gap-x-[2px] px-1 bg-black rounded text-white text-tiny mobile:text-xs touch-device:text-sm border border-[#493422] items-center w-fit mx-auto",
          !showNumber && "py-[1px]",
        )}
      >
        {iconUrl && (
          <img
            className="my-auto w-[9px] h-[9px] touch-device:w-[13px] touch-device:h-[13px]"
            src={iconUrl}
            alt={t("alt.coin")}
          />
        )}

        {label && (
          <span className="leading-[9px] touch-device:leading-3 py-[2px]">
            {label}
          </span>
        )}
        {showNumber && (
          <span className="leading-[9px] touch-device:leading-3 py-[2px]">
            {convertPlayerPrice(displayPrice)}
          </span>
        )}
        {owned && (
          <div className="w-2 h-2 touch-device:w-3 touch-device:h-3 shrink-0">
            {ownedIcon}
          </div>
        )}
        {untradeable && (
          <div className="w-2 h-2 touch-device:w-3 touch-device:h-3 shrink-0">
            {untradeableIcon}
          </div>
        )}
      </div>
    </div>
  );

  const playStylePlusSection = (
    <div
      className={cn(
        "absolute mobile:left-1 flex flex-col touch-device:left-[6px]",
        largeMobile ? "left-[6px]" : "left-[3px]",
        playStylesPlus?.length > 2 ? "top-[42%]" : "top-[50%]",
        playStylesPlus?.length > 3 ? "top-[35%]" : "top-[50%]",
      )}
    >
      {playStylesPlus?.map((playStylePlusId, index) => (
        <div
          className={cn(
            "mobile:w-3.5 mobile:h-3.5 touch-device:size-5",
            largeMobile ? "w-3.5 h-3.5" : "w-2.5 h-2.5",
            playStylesPlus?.length > 3 && index > 0 && "-mt-1",
          )}
          key={`${playStylePlusId}-${index}`}
        >
          <PlayStylePlus
            primaryColor={bottomText}
            secondaryColor={strip}
            id={playStylePlusId}
          />
        </div>
      ))}
    </div>
  );

  const faceStatKeys =
    preferredPosition === "GK" ? FACE_STAT_KEYS.gk : FACE_STAT_KEYS.outfield;
  const faceShort = (i: number) => playerAttributes[faceStatKeys[i]]?.short;
  const attributes_ = (position: string) => (
    <div
      className={cn(
        "flex flex-row gap-x-[4px] touch-device:gap-x-[6px] text-xxtiny touch-device:text-xtiny leading-[9px] touch-device:leading-[10px] mt-1 text-center",
        // An autograph card shows no face stats; the row still sizes the
        // bottom area so the crests sit where they do on a normal card.
        cardFont === "jalliya" && "invisible",
      )}
      style={{
        fontFamily: "CruyffCondensedMedium",
      }}
    >
      <div className="flex flex-col">
        <div>{faceShort(0)}</div>
        <div
          className="touch-device:text-tiny text-xtiny"
          style={{
            fontFamily: "CruyffMedium",
          }}
        >
          {attributes?.[0]}
        </div>
      </div>
      <div className="flex flex-col">
        <div>{faceShort(1)}</div>
        <div
          className="touch-device:text-tiny text-xtiny"
          style={{
            fontFamily: "CruyffMedium",
          }}
        >
          {attributes?.[1]}
        </div>
      </div>
      <div className="flex flex-col">
        <div>{faceShort(2)}</div>
        <div
          className="touch-device:text-tiny text-xtiny"
          style={{
            fontFamily: "CruyffMedium",
          }}
        >
          {attributes?.[2]}
        </div>
      </div>
      <div className="flex flex-col">
        <div>{faceShort(3)}</div>
        <div
          className="touch-device:text-tiny text-xtiny"
          style={{
            fontFamily: "CruyffMedium",
          }}
        >
          {attributes?.[3]}
        </div>
      </div>
      <div className="flex flex-col">
        <div>{faceShort(4)}</div>
        <div
          className="touch-device:text-tiny text-xtiny"
          style={{
            fontFamily: "CruyffMedium",
          }}
        >
          {attributes?.[4]}
        </div>
      </div>
      <div className="flex flex-col">
        <div>{faceShort(5)}</div>
        <div
          className="touch-device:text-tiny text-xtiny"
          style={{
            fontFamily: "CruyffMedium",
          }}
        >
          {attributes?.[5]}
        </div>
      </div>
    </div>
  );

  const clubLeagueCountry = (
    <div
      className={cn(
        "w-full mx-auto justify-center gap-x-1 relative mobile:mt-[2px]",
        largeMobile ? "mt-[2px]" : "mt-[4px]",
      )}
    >
      <img
        alt={t("alt.country")}
        className="absolute touch-device:w-4 w-3 h-[6px] left-[27%] bottom-[-6px] touch-device:bottom-[-10px] touch-device:h-2  touch-device:left-[25%] mt-[2px]"
        src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/countries/${countryId}.png`}
      />
      <img
        alt={t("alt.league")}
        className="absolute w-2 h-2 -translate-x-1/2 left-1/2 touch-device:w-3 touch-device:h-3"
        src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/leagues/${
          isLight ? "" : "dark/"
        }${leagueId}.png`}
      />
      <img
        alt={t("alt.club")}
        className="absolute touch-device:w-3 touch-device:h-3 h-2 w-2 right-[32%] touch-device:right-[30%]"
        src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/clubs/${
          isLight ? "" : "dark/"
        }${clubId}.png`}
      />
    </div>
  );

  const bottomArea = (
    <div
      className={cn(
        "absolute flex flex-col top-[66%] translate-x-1/2 right-1/2 w-full mobile:w-auto",
        largeMobile ? "w-auto" : "w-full",
      )}
      style={{
        color: bottomText,
      }}
    >
      <div
        className={cn(
          // Font size first: tailwind-merge drops `leading-*` that precedes a
          // `text-<size>`. The signature keeps the normal name's line box and
          // overflows it, so the blank stats row and the crests stay put.
          cardFont === "jalliya"
            ? "text-xs touch-device:text-lg translate-y-1"
            : "text-xtiny touch-device:text-xs",
          "w-full mobile:w-auto whitespace-nowrap leading-[8px] mobile:leading-[1px] text-center touch-device:leading-[4px] mt-[2px]",
          largeMobile ? "leading-[1px]" : "leading-[8px]",
        )}
        style={{
          fontFamily: cardFont === "jalliya" ? "JalliyaSeven" : "CruyffMedium",
        }}
      >
        {cardName}
      </div>
      {!isMobile && attributes_(preferredPosition)}
      {clubLeagueCountry}
    </div>
  );

  const evolutionsIconSection = (
    <div
      className={cn(
        "absolute mobile:bottom-3 touch-device:bottom-5 right-2 touch-device:right-3 flex flex-col  mobile:w-4 touch-device:w-4.5 mobile:h-4 touch-device:h-4.5",
        largeMobile ? "w-4.5 h-4.5 bottom-2.5" : "w-3.5  h-3.5 bottom-2",
      )}
    >
      <EvolutionsIcon outlineColor={topText} backgroundColor={strip} />
    </div>
  );

  // `relative` only so it out-paints the absolutely positioned foil layer.
  const CostSection = () => (
    <div className="relative flex flex-row items-center gap-x-1">
      <img
        className="size-4"
        src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/misc/coin-tmp.png`}
      />
      {formatEvoCost(cost.coins)}
      <img
        className="size-4"
        src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc24/misc/points.png`}
      />
      {formatEvoCost(cost.points)}
      {tokenCosts.map((token) => (
        <span
          key={token.eventTokenId}
          className="flex flex-row items-center gap-x-1"
        >
          <img
            className="size-4 object-contain"
            alt={token.name}
            src={token.iconUrl}
          />
          {formatEvoCost(token.amount)}
        </span>
      ))}
    </div>
  );

  const skillMovesWeakFootSection = (
    <div className="absolute bottom-16 right-0 mobile:right-1 flex flex-col gap-y-1 text-xs">
      <div
        className="flex flex-row items-center gap-x-0.25 border px-0.75 rounded h-3.5"
        style={{
          backgroundColor: strip,
          borderColor: topText,
          color: topText,
        }}
      >
        <div className="text-xtiny mobile:text-[10px] leading-0">
          {skillMoves}
        </div>
        <div className="w-2 h-2 shrink-0">{star}</div>
      </div>
      <div
        className="flex flex-row items-center gap-x-0.25 border px-0.75 rounded h-3.5"
        style={{
          backgroundColor: strip,
          borderColor: topText,
          color: topText,
        }}
      >
        <div className="text-xtiny mobile:text-[10px] leading-0">
          {weakFoot}
        </div>
        <div className="w-2 h-2 shrink-0">{star}</div>
      </div>
    </div>
  );

  return (
    <div
      className={cn(
        "relative mobile:w-28 touch-device:w-36 text-[#412b18] ",
        largeMobile ? "w-28 aspect-129/179" : "w-20",
        onClick && "cursor-pointer",
      )}
      onClick={onClick ? onClick : undefined}
    >
      {/* eslint-disable-next-line i18next/no-literal-string -- asset id, not copy */}
      <img alt="type" src={cardUrl} />
      {/* Over the version art, under every sibling below — which must be positioned. */}
      <CardEffects
        foil={foil}
        cosmetics={cosmetics}
        cardUrl={cardUrl}
        playOnShow={playOnShow}
      />
      {!hidePrice && priceSection}
      {playerImage}
      {topLeftArea}
      {bottomArea}
      {skillMoves && weakFoot && skillMovesWeakFootSection}
      {playStylesPlus?.length > 0 && playStylePlusSection}
      {isEvoPlayer && evolutionsIconSection}
      {cost && <CostSection />}
    </div>
  );
};

// Memoized: a heavy card rendered in long lists. Skips re-render when props are
// shallow-equal (callers must pass stable array/object props to benefit).
export default FC24Player;
